import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "./api-client";

/**
 * @param {{ page?: number; limit?: number; isActive?: boolean; search?: string }} params
 */
function fetchSupervisors(params) {
  const { page = 1, limit = 20, isActive, search } = params;
  const query = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  if (isActive !== undefined && isActive !== null) {
    query.append("isActive", String(isActive));
  }
  if (search) {
    query.append("search", search);
  }
  return apiClient({
    url: `users/supervisors?${query.toString()}`,
    method: "GET",
  });
}

export function useSupervisorsList(params) {
  const { page, limit, isActive, search } = params;
  return useQuery({
    queryKey: ["supervisors", page, limit, isActive, search],
    queryFn: () => fetchSupervisors({ page, limit, isActive, search }),
    placeholderData: (prev) => prev,
  });
}

/** Active supervisors for booking shift pattern picker (single fetch, high limit). */
export function useSupervisorsListForBooking(options = {}) {
  return useSupervisorsList({
    page: 1,
    limit: 100,
    isActive: true,
    ...options,
  });
}

export function parseSupervisorsListResponse(data) {
  const nested = data?.data ?? data;
  const rows = Array.isArray(nested?.data) ? nested.data : [];
  return rows.filter((row) => row?.supervisorIsActive !== false);
}

export function useCreateSupervisor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      apiClient({ url: "users/supervisors", method: "POST", data: payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["supervisors"] });
    },
  });
}

export function useUpdateSupervisor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, payload }) =>
      apiClient({
        url: `users/supervisors/${encodeURIComponent(slug)}`,
        method: "PATCH",
        data: payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["supervisors"] });
    },
  });
}

export function useSetSupervisorActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, isActive }) =>
      apiClient({
        url: `users/supervisors/${encodeURIComponent(slug)}/active`,
        method: "PATCH",
        data: { isActive },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["supervisors"] });
    },
  });
}
